import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { SITE, NAV_LINKS } from "@/config/site";

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const linkClass = ({ isActive }) =>
        `text-sm transition-colors duration-300 hover:text-sage ${
            isActive ? "font-medium text-sage" : "text-clay"
        }`;

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "border-b border-border/60 bg-cream/90 shadow-[0_4px_24px_rgba(67,46,30,0.06)] backdrop-blur-md"
                    : "bg-cream/70 backdrop-blur-sm"
            }`}
        >
            <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 md:h-20 md:px-8">
                <Link to="/" data-testid="nav-logo" className="group flex flex-col leading-none">
                    <span className="font-serif text-lg font-medium text-ink transition-colors duration-300 group-hover:text-sage md:text-xl">
                        Equilibrio del Sé - Sara Solferini
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-fog">{SITE.brandSuffix}</span>
                </Link>

                <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigazione principale">
                    {NAV_LINKS.map((link) => (
                        <NavLink key={link.path} to={link.path} data-testid={link.testid} className={linkClass} end={link.path === "/"}>
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Button
                        asChild
                        data-testid="nav-btn-prenota"
                        className="rounded-full bg-sage px-5 text-cream transition-colors duration-300 hover:bg-sage-dark"
                    >
                        <Link to="/prenota">Prenota</Link>
                    </Button>

                    <Sheet>
                        <SheetTrigger asChild>
                            <button
                                data-testid="nav-mobile-menu-button"
                                aria-label="Apri il menu"
                                className="rounded-full border border-border p-2 text-ink transition-colors duration-300 hover:border-sage hover:text-sage lg:hidden"
                            >
                                <Menu className="h-5 w-5" strokeWidth={1.5} />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[85vw] border-l-border bg-cream" data-testid="nav-mobile-drawer">
                            <div className="mt-10 flex flex-col gap-1">
                                {NAV_LINKS.map((link) => (
                                    <SheetClose asChild key={link.path}>
                                        <NavLink
                                            to={link.path}
                                            data-testid={`${link.testid}-mobile`}
                                            end={link.path === "/"}
                                            className={({ isActive }) =>
                                                `rounded-xl px-4 py-3 font-serif text-2xl font-light transition-colors duration-300 ${
                                                    isActive ? "bg-sage-light text-sage-dark" : "text-ink hover:bg-sand"
                                                } ${location.pathname === link.path ? "" : ""}`
                                            }
                                        >
                                            {link.label}
                                        </NavLink>
                                    </SheetClose>
                                ))}
                                <SheetClose asChild>
                                    <Link
                                        to="/prenota"
                                        data-testid="nav-btn-prenota-mobile"
                                        className="mt-6 rounded-full bg-sage px-6 py-4 text-center font-medium text-cream transition-colors duration-300 hover:bg-sage-dark"
                                    >
                                        Prenota il tuo trattamento
                                    </Link>
                                </SheetClose>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
